import {
    createSlice,
    createAsyncThunk,
    createEntityAdapter,
} from "@reduxjs/toolkit";
import { request } from "@/utils/api";

// Create entity adapter
const callbackAdapter = createEntityAdapter({
    selectId: (callback) => callback._id,
    sortComparer: (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
});

// Async Thunks
export const fetchCallRequest = createAsyncThunk(
    "callback/fetch",
    async (_, { rejectWithValue }) => {
        try {
            const response = await request({
                endpoint: `/admin/support/getRequestCallback`,
                method: "GET",
            });
            if (!response.success || !Array.isArray(response.data)) {
                return rejectWithValue(response.error?.message || "Failed to fetch callback requests");
            }
            return response.data;
        }
        catch ({ error }) {
            return rejectWithValue(error.message || "Failed to fetch callback requests");
        }
    }
);

export const removeCallBack = createAsyncThunk(
    "callback/remove",
    async (callbackId, { rejectWithValue }) => {
        try {
            const response = await request({
                endpoint: "/admin/support/deleteRequestCallback",
                method: "DELETE",
                params: { callbackId },
            });

            if (!response.success) {
                return rejectWithValue(response.error?.message || "Failed to remove callback request.");
            }

            return callbackId;
        } catch ({ error }) {
            return rejectWithValue(error.message || "An unexpected error occurred while removing the callback request.");
        }
    }
);

// Initial state
const initialState = callbackAdapter.getInitialState({
    loading: false,
    error: null,
});

// Slice
const callbackSlice = createSlice({
    name: "callbackRequest",
    initialState,
    reducers: {
        clearCallbacks: (state) => {
            callbackAdapter.removeAll(state);
            state.loading = false;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch Call Request
            .addCase(fetchCallRequest.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCallRequest.fulfilled, (state, action) => {
                callbackAdapter.setAll(state, action.payload);
                state.loading = false;
            })
            .addCase(fetchCallRequest.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Remove Call Back
            .addCase(removeCallBack.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(removeCallBack.fulfilled, (state, action) => {
                callbackAdapter.removeOne(state, action.payload);
                state.loading = false;
            })
            .addCase(removeCallBack.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

// Update selectors export
export const {
    selectAll: selectAllCallbacks,
    selectById: selectCallbackById,
    selectIds: selectCallbackIds,
} = callbackAdapter.getSelectors((state) => state.callbackRequest);

// Update actions export
export const { clearCallbacks } = callbackSlice.actions;
export default callbackSlice.reducer;
